import { useApp } from "@/context/AppContext";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Clock, Calendar, Type, Bell, Database, Download } from "lucide-react";
import { SharingSettings } from "@/components/SharingSettings";
import { ApiKeysSettings } from "@/components/ApiKeysSettings";
import { toast } from "sonner";

function Section({ icon: Ico, title, children }) {
  return (
    <div className="rounded-3xl p-5 mb-4" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }}>
      <div className="flex items-center gap-2 mb-4">
        <Ico size={18} style={{ color: "var(--accent-primary)" }} />
        <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div><p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{label}</p>{hint && <p className="text-xs" style={{ color: "var(--text-muted)" }}>{hint}</p>}</div>
      {children}
    </div>
  );
}

function Segment({ value, options, onChange, testId }) {
  return (
    <div className="flex p-1 rounded-xl" style={{ background: "var(--bg-timeline-track)" }} data-testid={testId}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} className="px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors"
          style={{ background: value === o.v ? "var(--accent-primary)" : "transparent", color: value === o.v ? "#fff" : "var(--text-secondary)" }}>{o.l}</button>
      ))}
    </div>
  );
}

export function SettingsPage() {
  const { settings, updateSettings, events, tasks, categories } = useApp();
  if (!settings) return null;
  const s = settings;
  const up = (patch) => updateSettings(patch);

  const exportData = async () => {
    const data = { events, tasks, categories, settings, exported_at: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "chronograph-backup.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Data exported");
  };

  const exportICS = () => {
    let ics = "BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//ChronoGraph//EN\n";
    events.forEach((e) => {
      const d = e.date.replace(/-/g, "");
      const sh = String(Math.floor(e.start_minutes / 60)).padStart(2, "0") + String(e.start_minutes % 60).padStart(2, "0") + "00";
      const eh = String(Math.floor(e.end_minutes / 60)).padStart(2, "0") + String(e.end_minutes % 60).padStart(2, "0") + "00";
      ics += `BEGIN:VEVENT\nUID:${e.id}\nDTSTART:${d}T${sh}\nDTEND:${d}T${eh}\nSUMMARY:${e.title}\n${e.location ? `LOCATION:${e.location}\n` : ""}END:VEVENT\n`;
    });
    ics += "END:VCALENDAR";
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "chronograph.ics"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Calendar exported (.ics)");
  };

  return (
    <div className="h-full overflow-y-auto p-4 lg:p-6">
      <h2 className="font-display text-2xl font-extrabold mb-1" style={{ color: "var(--text-primary)" }}>Settings</h2>
      <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>Configure ChronoGraph to your workflow</p>

      <div className="max-w-2xl">
        <SharingSettings />
        <ApiKeysSettings />

        <Section icon={Clock} title="Time & Format">
          <Row label="Time Format"><Segment value={s.time_format} options={[{ v: "12", l: "12h" }, { v: "24", l: "24h" }]} onChange={(v) => up({ time_format: v })} testId="setting-time-format" /></Row>
          <Row label="Week Starts On"><Segment value={s.week_start} options={[{ v: "mon", l: "Monday" }, { v: "sun", l: "Sunday" }]} onChange={(v) => up({ week_start: v })} testId="setting-week-start" /></Row>
        </Section>

        <Section icon={Calendar} title="Timeline">
          <Row label="First Visible Hour" hint={`${s.day_start_hour}:00`}>
            <div className="w-40"><Slider value={[s.day_start_hour]} min={0} max={12} step={1} onValueChange={([v]) => up({ day_start_hour: Math.min(v, s.day_end_hour - 1) })} data-testid="setting-day-start" /></div>
          </Row>
          <Row label="Last Visible Hour" hint={`${s.day_end_hour}:00`}>
            <div className="w-40"><Slider value={[s.day_end_hour]} min={13} max={24} step={1} onValueChange={([v]) => up({ day_end_hour: Math.max(v, s.day_start_hour + 1) })} data-testid="setting-day-end" /></div>
          </Row>
        </Section>

        <Section icon={Type} title="Appearance">
          <Row label="Font Size" hint={`${Math.round((s.font_scale || 1) * 100)}%`}>
            <div className="w-40"><Slider value={[(s.font_scale || 1) * 100]} min={85} max={125} step={5} onValueChange={([v]) => up({ font_scale: v / 100 })} data-testid="setting-font-scale" /></div>
          </Row>
        </Section>

        <Section icon={Bell} title="Notifications">
          <Row label="In-app Reminders" hint="Show visual reminders for upcoming events">
            <Switch checked={s.notifications_enabled} onCheckedChange={(v) => up({ notifications_enabled: v })} data-testid="setting-notifications" />
          </Row>
        </Section>

        <Section icon={Database} title="Data & Backup">
          <Row label="Export Backup" hint="Download all your data as JSON">
            <Button onClick={exportData} variant="ghost" className="gap-2" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)" }} data-testid="setting-export-json"><Download size={15} /> JSON</Button>
          </Row>
          <Row label="Export Calendar" hint="Standard .ics file for other calendar apps">
            <Button onClick={exportICS} variant="ghost" className="gap-2" style={{ background: "var(--bg-timeline-track)", color: "var(--text-primary)" }} data-testid="setting-export-ics"><Download size={15} /> .ics</Button>
          </Row>
        </Section>
      </div>
    </div>
  );
}
